// A lista de cartões do viewport estreito de /inscricao/:tipo e /agenda/:tipo:
// um cartão por simulado, com o mesmo conteúdo e as mesmas duas ações
// condicionais que `InscricaoSimulado.view.tsx` já desenha na coluna "Ação"
// da tabela legada (design.md, Decisão 4). Puramente apresentacional — não
// busca nada; a view decide a lista e os dois callbacks.
import type { Simulado } from '../../../sdk/@types/Simulado';

export interface SimuladoInscricaoCardsProps {
  simulados: Simulado.Simulado[];
  onInscrever: (simulado: Simulado.Simulado) => void;
  onDesistir: (simulado: Simulado.Simulado) => void;
}

/**
 * A única regra de formatação de data da tela: `InscricaoSimulado.view.tsx`
 * importa esta função para as colunas Início/Fim da tabela em vez de
 * duplicá-la, para que as duas variantes não possam divergir.
 */
export function formatarDataHora(value?: string): string {
  return value
    ? `${new Date(value).toLocaleDateString('pt-BR')} ${new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : 'Indefinido';
}

/** `registered === false`, nunca `!registered` — mesma função que a tabela (view) importa. */
export function ofereceInscricao(simulado: Simulado.Simulado): boolean {
  return simulado.registered === false;
}

/** `registered && end <= agora` — mesma função que a tabela (view) importa. */
export function ofereceDesistencia(simulado: Simulado.Simulado): boolean {
  return Boolean(simulado.registered) && new Date(simulado.end as string) <= new Date();
}

function Campo({ label, valor, destaque }: { label: string; valor?: string; destaque?: boolean }) {
  return (
    <div>
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className={destaque ? 'text-[15px] font-semibold text-ink' : 'text-[13px] font-medium text-ink'}>
        {valor}
      </dd>
    </div>
  );
}

interface SimuladoInscricaoCardProps {
  simulado: Simulado.Simulado;
  onInscrever: (simulado: Simulado.Simulado) => void;
  onDesistir: (simulado: Simulado.Simulado) => void;
}

function SimuladoInscricaoCard({ simulado, onInscrever, onDesistir }: SimuladoInscricaoCardProps) {
  const inscrever = ofereceInscricao(simulado);
  const desistir = ofereceDesistencia(simulado);

  return (
    <div className="rounded-card border border-edge bg-surface p-4">
      <dl className="flex flex-col gap-2.5">
        <Campo label="Descrição" valor={simulado.description} destaque />
        <Campo label="Tipo" valor={simulado.type} />
        <Campo label="Situação" valor={simulado.situation} />
        <Campo label="Início" valor={formatarDataHora(simulado.start)} />
        <Campo label="Fim" valor={formatarDataHora(simulado.end)} />
      </dl>

      {(inscrever || desistir) && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-edge pt-3">
          {inscrever && (
            <button
              type="button"
              onClick={() => onInscrever(simulado)}
              className="inline-flex h-9 items-center justify-center rounded-input bg-brand px-4 text-sm font-semibold text-brand-contrast hover:brightness-110"
            >
              Inscrever
            </button>
          )}
          {desistir && (
            <button
              type="button"
              onClick={() => onDesistir(simulado)}
              className="inline-flex h-9 items-center justify-center rounded-input border border-danger/40 bg-surface px-4 text-sm font-semibold text-danger-ink hover:bg-danger/[0.08]"
            >
              Desistir
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SimuladoInscricaoCards({ simulados, onInscrever, onDesistir }: SimuladoInscricaoCardsProps) {
  if (!simulados.length) {
    return (
      <div className="rounded-card border border-edge bg-surface p-4 text-[13px] text-ink-muted">
        Nenhum simulado disponível.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {simulados.map((simulado, index) => (
        <SimuladoInscricaoCard
          key={simulado.id ?? index}
          simulado={simulado}
          onInscrever={onInscrever}
          onDesistir={onDesistir}
        />
      ))}
    </div>
  );
}

export default SimuladoInscricaoCards;
